import { Fragment, useEffect, useState } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import { PageWrapper } from '../Styled';
import { toastError } from 'components/Toast';
import BackToTop from 'components/Measure/BackToTop';
import MainContent from 'components/MainContent';
import PageHeader from 'components/Measure/PageHeader';
import PageBackground from 'components/PageBackground';
import { Icon, Button } from 'semantic-ui-react';
import RouteHelpers from 'helpers/routes';
import PageLoader from 'components/PageLoader';
import { Link, useNavigate } from 'react-router-dom';
import InfoBox from 'components/Measure/View/InfoBox';
import SubmitModal from 'components/Measure/Summary/SubmitModal';
import ClassroomExamples from 'components/Measure/ClassroomExamples';
import ObservationFeedback from 'components/Measure/ObservationFeedback';
import ObservationScores from 'components/Measure/ObservationScores';
import AttachmentViewer from 'components/Measure/View/ExamplesAndNotes/Attachments/AttachmentViewer';
import ConfirmationModal, {
  CallbackProps
} from 'components/Modals/ConfirmationModal';
import { ObservationNotes } from 'components/Measure/ObservationNotes';
import { ServerError } from 'types/api/ServerError';
import { genericError, jobIdError } from './utils';
import { useGetSettings } from 'actions/envScales/settings';
import { TARGET_TYPE } from 'types/api/envScales/ObservationFeedback';
import RequestUpdatesModal from 'components/Measure/Summary/RequestUpdatesModal';
import ScoreSummary from 'components/Measure/View/ScoreSummary';
import FooterNav from 'components/Measure/View/FooterNav';
import { useValidations } from 'components/Measure/View/ValidationsContext';
import { useDCRoles } from 'pages/Measure/Completed/Completed';
import useCurrentUser from 'hooks/useCurrentUser';

import {
  approveAssessment,
  getAssessment
} from 'actions/envScales/assessments';

import {
  Section,
  SectionActions,
  ActionWrapper
} from 'components/Measure/View/Styled';

interface CyclesReviewProps {
  /** CLASS Assessment ID */
  assessmentId: number;

  /** Account GUID. */
  accountGuid: string;

  /** If `true`, applies changes to work alongside sidebar navigation. */
  isContainedInSidenav?: boolean;
}

function CyclesReview({
  assessmentId,
  accountGuid,
  isContainedInSidenav
}: CyclesReviewProps) {
  const navigate = useNavigate();
  const { isDCAdmin } = useDCRoles();
  const currentUser = useCurrentUser();

  const {
    run: assessmentRun,
    data: assessment,
    isPending: isAssessmentPending
  } = useAsync();

  const { data: settings, isSuccess: isSettingsSuccess } =
    useGetSettings(accountGuid);

  const [isSubmitModalOpen, setSubmitModalOpen] = useState(false);
  const [attachmentHasErrors, setAttachmentHasErrors] = useState(false);
  const [approveErrors, setApproveErrors] = useState<JSX.Element[] | null>(
    null
  );
  const isAwaitingApproval = assessment?.status === 'awaiting_approval';
  const isInProgress = assessment?.status === 'open';
  const isCreator = assessment?.created_by_guid === currentUser.guid;
  const { validate } = useValidations();
  const isFutureDate = new Date(assessment?.taken_at) >= new Date();

  useEffect(() => {
    assessmentRun(getAssessment(assessmentId, { expanded: true }));
  }, [assessmentRun, assessmentId]);

  function handleSubmit() {
    setSubmitModalOpen(false);
    navigate(RouteHelpers.getPath('measure-completed', { id: assessment.id }), {
      replace: true
    });
  }

  function handleSubmitErrors(error: ServerError) {
    const errorsComponents = [];

    for (const key in error.errors) {
      const errorObject = error.errors[key];
      if (key === 'job_id') {
        errorsComponents.push(jobIdError(assessment.id));
      } else if (key === 'attachments') {
        setAttachmentHasErrors(true);
        errorsComponents.push(genericError(errorObject[0]));
      } else {
        if (typeof errorObject === 'string') {
          errorsComponents.push(genericError(errorObject));
        } else if (Array.isArray(errorObject)) {
          errorsComponents.push(genericError(errorObject[0]));
        }
      }
    }

    return errorsComponents;
  }

  async function approve({ closeModal, setLoading }: CallbackProps) {
    try {
      await approveAssessment(assessment.id);
      closeModal();
      navigate(
        RouteHelpers.getPath('measure-completed', { id: assessment.id }),
        { replace: true }
      );
    } catch (error: any) {
      if (error?.response?.data?.errors) {
        setApproveErrors(handleSubmitErrors(error.response.data));
      }
      setLoading(false);
    }
  }

  if (!isSettingsSuccess || isAssessmentPending) {
    return <PageLoader />;
  }

  const { strength_and_growth_enabled: displayClassroomExamples } = settings;

  function isFeedbackEnabled() {
    return settings?.dimension_feedback || settings?.domain_feedback;
  }

  function getTargetType(): TARGET_TYPE {
    if (settings?.dimension_feedback) {
      return 'Dimension';
    } else {
      return 'Domain';
    }
  }

  async function handleSubmitForNewLayout() {
    const shouldOpenSubmitModal = await validate();
    if (shouldOpenSubmitModal && !isFutureDate) {
      setSubmitModalOpen(true);
    } else if (isFutureDate) {
      toastError({
        description: i18n.ft(messages.dateError)
      });
    } else {
      toastError({
        description: i18n.ft(messages.submitError)
      });
    }
  }

  const targetType = getTargetType();
  const displayFeedback = isFeedbackEnabled();
  const observationId = assessment.observation!.id;

  const MainWrapper = isContainedInSidenav ? Fragment : MainContent;
  const Wrapper = isContainedInSidenav ? Fragment : PageWrapper;
  const BackgroundWrapper = isContainedInSidenav ? Fragment : PageBackground;

  return (
    <MainWrapper maxWidth={1280}>
      <Wrapper>
        <SubmitModal
          open={isSubmitModalOpen}
          assessmentId={assessment.id}
          onClose={() => setSubmitModalOpen(false)}
          onErrors={handleSubmitErrors}
          onSuccess={handleSubmit}
        />

        {isContainedInSidenav ? null : (
          <>
            {isAwaitingApproval ? (
              <PageHeader
                title={`${assessment.assessment_template.name} ${i18n.ft(
                  messages.approve
                )}`}
              />
            ) : (
              <PageHeader
                title={`${assessment.assessment_template.name} ${i18n.ft(
                  messages.title
                )}`}
                subtitle={i18n.ft(messages.subtitle)}
              />
            )}
          </>
        )}

        <BackgroundWrapper>
          {isContainedInSidenav ? null : (
            <InfoBox
              data={assessment}
              showButton={false}
              extended
              isAdmin={isDCAdmin}
            />
          )}

          {assessment.includes_environments && (
            <div className={isContainedInSidenav ? '' : 'mt-8 sm:mt-10'}>
              <ScoreSummary
                assessmentId={assessment.id}
                infoData={assessment}
                isEditable={false}
              />
            </div>
          )}

          <ObservationScores fitted={assessment.includes_environments} />

          {settings.notes_enabled && (
            <ObservationNotes assessment={assessment} />
          )}
          {displayClassroomExamples && (
            <ClassroomExamples
              showEdit={false}
              observation={assessment.observation}
            />
          )}

          {displayFeedback && (
            <ObservationFeedback
              assessmentId={assessment.id}
              observationId={observationId}
              targetType={targetType}
              showEdit={false}
            />
          )}

          <AttachmentViewer showEdit={false} hasErrors={attachmentHasErrors} />

          <BackToTop />

          {isContainedInSidenav ? (
            <FooterNav
              rightSideElement={
                isCreator && isInProgress ? (
                  <div className="md:w-52">
                    <Button
                      fluid
                      color="blue"
                      onClick={handleSubmitForNewLayout}
                      content={i18n.ft(messages.submit)}
                    />
                  </div>
                ) : undefined
              }
            />
          ) : (
            <Section className="mt-2 rounded-b-xl">
              <SectionActions>
                <ActionWrapper>
                  <Button
                    fluid
                    basic
                    as={Link}
                    to={RouteHelpers.getPath('measure')}
                  >
                    <Icon name="arrow alternate circle left outline" />
                    {i18n.ft(messages.return)}
                  </Button>
                </ActionWrapper>

                {isAwaitingApproval ? (
                  <>
                    <ActionWrapper>
                      <RequestUpdatesModal assessmentId={assessment.id} />
                    </ActionWrapper>

                    <ActionWrapper>
                      <ConfirmationModal
                        title={i18n.ft(messages.approve)}
                        message={
                          <>
                            {i18n.ft(messages.modal.messageApprove)}
                            {approveErrors &&
                              approveErrors.map((error, key) => (
                                <div key={key}>{error}</div>
                              ))}
                          </>
                        }
                        confirmButton={i18n.ft(messages.modal.buttonApprove)}
                        icon="tasks"
                        confirmColor="blue"
                        triggerNode={
                          <Button
                            fluid
                            color="green"
                            content={i18n.ft(messages.approve)}
                          />
                        }
                        onOpen={setIsOpenModal => {
                          setApproveErrors(null);
                          setIsOpenModal(true);
                        }}
                        onConfirm={approve}
                      />
                    </ActionWrapper>
                  </>
                ) : (
                  isCreator &&
                  isInProgress && (
                    <ActionWrapper>
                      <Button
                        fluid
                        color="blue"
                        onClick={() => setSubmitModalOpen(true)}
                        content={i18n.ft(messages.submit)}
                      />
                    </ActionWrapper>
                  )
                )}
              </SectionActions>
            </Section>
          )}
        </BackgroundWrapper>
      </Wrapper>
    </MainWrapper>
  );
}

export default CyclesReview;
